import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Modal, FormInput, FormSelect, FormTextarea, Button, SearchableSelect } from "@/components/ui";
import { INDIAN_STATES as states, REFERENCE_SOURCES as referenceSources, Verve_REQUIREMENTS, LEAD_STAGES } from "@/constants/globalConstants";
import { Controller } from "react-hook-form";
import { leadSchema } from "../validations/lead.schema";
import { useOffices } from "@/hooks/useOffices";
import { numberToWords } from "@/lib/formatters";

export const AddLead = ({ isOpen, onClose, onSubmit, initialData, teamMembers, user }) => {
  const { data: offices = [] } = useOffices();

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    control,
    formState: { errors, isSubmitting }
  } = useForm({
    resolver: zodResolver(leadSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      phone: "",
      email: "",
      address1: "",
      address2: "",
      city: "",
      district: "",
      state: "",
      pincode: "",
      referenceType: "Direct",
      notes: "",
      requirementType: "",
      propertyType: "",
      budget: "",
      assignedTo: "",
      officeLocation: ""
    }
  });

  const selectedRefType = watch("referenceType");
  const selectedRequirementType = watch("requirementType");
  const selectedStatus = watch("status") || "new";

  const homeOptions = [
    "1 BHK",
    "2 BHK",
    "3 BHK",
    "4 BHK / Villa",
    "Living Room / Hall",
    "Master Bedroom",
    "Guest Bedroom",
    "Kids Room"
  ];
  const commercialOptions = [
    "Office",
    "Shop / Retail",
    "Restaurant / Cafe",
    "Showroom",
    "Clinic / Hospital",
    "Warehouse",
    "Factory",
    "Educational Institution"
  ];
  const currentTypeOptions = selectedRequirementType === "Commercial" ? commercialOptions : homeOptions;

  const roleStr = (user?.role || "").toLowerCase().trim().replace(/[-_\s]+/g, "-");
  const isSalesExec = roleStr === "sales-exec" || roleStr === "sale-exec" || roleStr === "sales-executive" || roleStr === "sales";

  // Filter only sales executives who match the lead's location
  const salesExecs = (teamMembers || []).filter(u => {
    const uRole = (u.role || "").toLowerCase().trim().replace(/[-_\s]+/g, "-");
    const isExec = uRole === 'sales-exec' || uRole === 'sale-exec' || uRole === 'sales-executive' || uRole === 'sales';

    // const leadLoc = (selectedCity || "").toLowerCase().trim();
    // const userLoc = (u.officeLocation || "").toLowerCase().trim();

    // Lenient match: true if either location is empty, OR if they match, OR if one contains the other
    // const matchesLocation = !userLoc ||
    //   userLoc.includes(leadLoc) ||
    //   leadLoc.includes(userLoc);

    return isExec;
  });

  useEffect(() => {
    if (isOpen) {
      if (initialData) {
        reset({
          ...initialData,
          phone: initialData.phoneNumber || initialData.phone || "",
          assignedTo: isSalesExec ? user?._id : (initialData.assignedTo?._id || initialData.assignedTo || "")
        });
      } else {
        reset({
          firstName: "", lastName: "", phone: "", email: "",
          address1: "", address2: "", city: "", district: "", state: "", pincode: "",
          referenceType: "Direct", notes: "", requirementType: "", propertyType: "", budget: "",
          assignedTo: isSalesExec ? user?._id : "",
          officeLocation: ""
        });
      }
    }
  }, [isOpen, initialData, reset, isSalesExec, user?._id]);

  const onFormSubmit = async (data) => {
    await onSubmit(data);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={initialData ? "Edit Lead Details" : "Add New Lead"} size="md-xl">
      <form onSubmit={handleSubmit(onFormSubmit)} className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3">
        <FormInput
          label="First Name"
          required
          {...register("firstName")}
          placeholder="First name"
          error={errors.firstName?.message}
          size="sm"
        />
        <FormInput
          label="Last Name"
          {...register("lastName")}
          placeholder="Last name"
          error={errors.lastName?.message}
          size="sm"
        />

        <FormInput
          label="Phone Number"
          required
          type="tel"
          {...register("phone")}
          onInput={(e) => {
            e.target.value = e.target.value.replace(/[^0-9]/g, '').slice(0, 10);
          }}
          maxLength={10}
          placeholder="10-digit number (no 0 or +91)"
          error={errors.phone?.message}
          size="sm"
        />
        <FormInput
          label="Email Address"
          type="email"
          {...register("email")}
          placeholder="Email address"
          error={errors.email?.message}
          size="sm"
        />

        <FormInput
          label="Address Line 1"
          {...register("address1")}
          placeholder="Street address, PO Box, etc."
          error={errors.address1?.message}
          size="sm"
        />
        <FormInput
          label="Address Line 2"
          {...register("address2")}
          placeholder="Apartment, suite, unit, etc."
          error={errors.address2?.message}
          size="sm"
        />

        <FormInput
          label="City"
          required
          {...register("city")}
          placeholder="City"
          error={errors.city?.message}
          size="sm"
        />
        <FormInput
          label="District"
          {...register("district")}
          placeholder="District"
          error={errors.district?.message}
          size="sm"
        />

        <FormSelect
          label="State"
          required
          {...register("state")}
          placeholder="-- Select State --"
          options={states}
          error={errors.state?.message}
          size="sm"
        />
        <FormInput
          label="PIN Code"
          type="tel"
          {...register("pincode")}
          onInput={(e) => {
            e.target.value = e.target.value.replace(/[^0-9]/g, '').slice(0, 6);
          }}
          maxLength={6}
          placeholder="6-digit PIN"
          error={errors.pincode?.message}
          size="sm"
        />

        <FormSelect
          label="Office Location"
          {...register("officeLocation")}
          placeholder="All Locations"
          options={(Array.isArray(offices) ? offices : []).map(o => ({ value: o.name, label: o.name }))}
          error={errors.officeLocation?.message}
          size="sm"
        />

        <FormSelect
          label={isSalesExec ? "Assigned To (You)" : "Assign Sales Executive (Optional)"}
          {...register("assignedTo")}
          disabled={isSalesExec}
          placeholder="-- Select Sales Executive --"
          options={salesExecs.map(m => ({ value: m._id, label: m.name }))}
          error={errors.assignedTo?.message}
          size="sm"
        />

        <Controller
          control={control}
          name="referenceType"
          render={({ field: { onChange, value } }) => (
            <SearchableSelect
              label="Reference Type"
              placeholder="-- Select Reference type --"
              value={value}
              onChange={onChange}
              error={errors.referenceType?.message}
              options={referenceSources}
            />
          )}
        />

        <FormSelect
          label={initialData ? "Current Status" : "Initial Status"}
          {...register("status")}
          error={errors.status?.message}
          disabled={true}
          placeholder="New Lead"
        >
          {LEAD_STAGES.map(s => (
            <option key={s.key} value={s.key}>{s.label}</option>
          ))}
        </FormSelect>

        {selectedRefType && selectedRefType !== "Direct" && (
          <div className="sm:col-span-2">
            <FormInput
              label="Referral Name / Details"
              {...register("referenceDetails")}
              placeholder="Enter name or details of the person who referred..."
              error={errors.referenceDetails?.message}
              size="sm"
            />
          </div>
        )}

        <FormSelect
          label="Requirement Type"
          {...register("requirementType")}
          error={errors.requirementType?.message}
          placeholder="-- Select Type --"
          options={Verve_REQUIREMENTS}
          size="sm"
        />

        <Controller
          control={control}
          name="propertyType"
          render={({ field: { onChange, value } }) => (
            <SearchableSelect
              label="Type"
              placeholder="-- Search --"
              value={value}
              onChange={onChange}
              error={errors.propertyType?.message}
              options={currentTypeOptions}
              allowCustom={true}
            />
          )}
        />

        <Controller
          control={control}
          name="budget"
          render={({ field: { onChange, value } }) => (
            <div className="flex flex-col gap-0.5 w-full">
              <FormInput
                label="Estimated Budget (₹)"
                type="text"
                value={value ? Number(value).toLocaleString('en-IN') : ''}
                onChange={(e) => {
                  const val = e.target.value.replace(/[^0-9]/g, '');
                  onChange(val ? Number(val) : '');
                }}
                error={errors.budget?.message}
                placeholder="e.g. 2,50,000"
                size="sm"
              />
              {value && !isNaN(value) && Number(value) > 0 ? (
                <div className="text-[10px] text-slate-500 mt-1 capitalize pl-1">
                  {numberToWords(Number(value))}
                </div>
              ) : null}
            </div>
          )}
        />

        <div className="sm:col-span-2">
          <FormTextarea
            label="Notes"
            rows={3}
            {...register("notes")}
            placeholder="Additional details..."
            error={errors.notes?.message}
            size="sm"
          />
        </div>

        <div className="flex gap-3 mt-6 pt-4 border-t border-slate-100 sm:col-span-2">
          <Button variant="outline" className="flex-1" onClick={onClose}>Cancel</Button>
          <Button
            type="submit"
            variant="orange"
            className="flex-1"
            loading={isSubmitting}
            disabled={isSubmitting}
          >
            {initialData ? "Update Lead" : "Add Lead"}
          </Button>
        </div>
      </form>
    </Modal>
  );
};
